import React, { useState, useEffect } from 'react'
import { Grid } from '@material-ui/core'
import PropertyCard from './PropertyCard'
import './ChangePropertyModal.css'
import Skeleton from 'react-loading-skeleton'
import { SearchPropertiesApi } from '../apis/SearchPropertiesApi'
import { JoinPropertyApi } from '../apis/JoinPropertyApi'
import { useData } from '../data/DataProvider'
import { GpModalShell, GpButton, Icon } from '../components/gp'

export default function ChangePropertyModal({ open, handleClose, myProperty }) {
    const data = useData()

    const [loading, setLoading] = useState(false)
    const [selectedProperty, setSelectedProperty] = useState(null)
    const [iAgree, setIAgree] = useState(false)
    const [showWarning, setShowWarning] = useState(true)
    const [properties, setProperties] = useState([])
    const [zip, setZip] = useState('')
    const [submitting, setSubmitting] = useState(false)

    const selectThis = (name) => {
        setSelectedProperty(
            properties.filter((property) => property.name === name)
        )
    }

    const getAllPropertiesByZip = (z) => {
        setLoading(true)
        SearchPropertiesApi.byZip(z)
            .then((data) => {
                setProperties(data.properties || [])
            })
            .finally(() => setLoading(false))
    }

    const requestPropertyAccess = () => {
        if (!selectedProperty) return
        setSubmitting(true)
        JoinPropertyApi.post(selectedProperty[0].propertyUUID)
            .then(() => {
                data.loadUserProperties()
                handleClose()
            })
            .finally(() => setSubmitting(false))
    }

    useEffect(() => {
        if (zip?.toString().length === 5) {
            getAllPropertiesByZip(zip)
        }
    }, [zip])

    useEffect(() => {
        if (!myProperty || !Object.keys(myProperty).length) {
            setIAgree(true)
            setShowWarning(false)
        } else {
            setIAgree(false)
            setShowWarning(true)
        }
    }, [myProperty])

    const canSubmit = !!selectedProperty && iAgree && !submitting
    const currentPropertyName = myProperty?.name || 'your current property'

    return (
        <GpModalShell
            open={open}
            onClose={handleClose}
            title="Change my property"
            width={720}
            footer={
                <>
                    <GpButton variant="ghost" onClick={handleClose} disabled={submitting}>
                        Cancel
                    </GpButton>
                    <GpButton
                        variant="primary"
                        onClick={requestPropertyAccess}
                        disabled={!canSubmit}
                    >
                        {submitting ? 'Requesting…' : 'Request charging access'}
                    </GpButton>
                </>
            }
        >
            <label
                style={{
                    display: 'block',
                    font: 'var(--gp-caption)',
                    color: 'var(--gp-ink-mute)',
                    letterSpacing: 0.4,
                    textTransform: 'uppercase',
                    marginBottom: 8,
                }}
            >
                New property zip code
            </label>
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '12px 14px',
                    background: 'var(--gp-card)',
                    border: '1px solid var(--gp-hairline)',
                    borderRadius: 'var(--gp-r-md)',
                }}
            >
                <span style={{ color: 'var(--gp-ink-mute)', display: 'inline-flex' }}>
                    <Icon name="home" size={18} />
                </span>
                <input
                    value={zip}
                    onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    placeholder="14217"
                    inputMode="numeric"
                    style={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        font: '500 16px/1 var(--gp-mono)',
                        color: 'var(--gp-ink)',
                        letterSpacing: 2,
                    }}
                />
            </div>

            {loading && (
                <Grid container spacing={2} style={{ marginTop: 16 }}>
                    {[0, 1, 2].map((i) => (
                        <Grid item md={4} xs={6} key={i}>
                            <Skeleton height="220px" borderRadius="14px" />
                        </Grid>
                    ))}
                </Grid>
            )}

            {!loading && properties?.length > 0 && (
                <Grid container spacing={2} style={{ marginTop: 16 }}>
                    {properties.map((property, index) => (
                        <Grid item md={4} xs={6} key={index}>
                            <PropertyCard
                                property={property}
                                selectThis={selectThis}
                                selectedProperty={selectedProperty}
                            />
                        </Grid>
                    ))}
                </Grid>
            )}

            {!loading && zip.length === 5 && properties?.length === 0 && (
                <div
                    style={{
                        marginTop: 16,
                        font: 'var(--gp-caption)',
                        color: 'var(--gp-ink-mute)',
                    }}
                >
                    No properties found for {zip}.
                </div>
            )}

            {showWarning && (
                <div
                    style={{
                        marginTop: 20,
                        background: '#FBF1E9',
                        border: '1px solid #F0DDC5',
                        borderRadius: 'var(--gp-r-md)',
                        padding: '14px 16px',
                        display: 'flex',
                        gap: 12,
                        alignItems: 'flex-start',
                    }}
                >
                    <div style={{ color: 'var(--gp-warn)', flexShrink: 0, marginTop: 1 }}>
                        <svg
                            width="20"
                            height="20"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.75"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01" />
                        </svg>
                    </div>
                    <div>
                        <div style={{ font: 'var(--gp-body-med)', color: 'var(--gp-ink)' }}>
                            Heads up — you'll lose access at your current residence
                        </div>
                        <div
                            style={{
                                font: 'var(--gp-caption)',
                                color: 'var(--gp-ink-mute)',
                                marginTop: 4,
                            }}
                        >
                            Switching properties revokes your charging permissions at{' '}
                            <strong style={{ color: 'var(--gp-ink)', fontWeight: 500 }}>
                                {currentPropertyName}
                            </strong>
                            . We'll review and confirm access at your new address.
                        </div>
                    </div>
                </div>
            )}

            {showWarning && (
                <label
                    style={{
                        marginTop: 16,
                        marginBottom: 4,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        cursor: 'pointer',
                        userSelect: 'none',
                    }}
                >
                    <input
                        type="checkbox"
                        checked={iAgree}
                        onChange={(e) => setIAgree(e.target.checked)}
                        className="termsCheckbox"
                        style={{ marginTop: 2, accentColor: 'var(--gp-brand)' }}
                    />
                    <span style={{ font: 'var(--gp-body)', color: 'var(--gp-ink)' }}>
                        I understand and want to proceed
                    </span>
                </label>
            )}
        </GpModalShell>
    )
}
